import { useState, useRef } from 'react'
import { Edit3 } from 'lucide-react'
import { Label } from '../ui/label'
import { BrandAutocomplete } from '../contributions/BrandAutocomplete'
import { useClickOutside } from '../../hooks/useClickOutside'
import { labelStyle, valueStyle } from '../../styles/typography'

interface InlineEditBrandProps {
  label: string
  value: string
  onChange: (brandName: string, brandId?: string) => void
  required?: boolean
  error?: boolean
  className?: string
}

export function InlineEditBrand({
  label,
  value,
  onChange,
  required = false,
  error = false,
  className = ''
}: InlineEditBrandProps) {
  const [isEditing, setIsEditing] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)

  useClickOutside(wrapperRef, () => setIsEditing(false), isEditing)

  if (isEditing) {
    return (
      <div className={className} ref={wrapperRef}>
        <Label style={labelStyle}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </Label>
        <BrandAutocomplete
          value={value}
          onChange={(brandName, brandId) => {
            onChange(brandName, brandId)
            // Don't close immediately - let user finish selection
          }}
          required={required}
        />
      </div>
    )
  }

  // Display mode (click to edit)
  return (
    <div
      className={`group cursor-pointer ${className}`}
      onClick={() => setIsEditing(true)}
    >
      <Label style={labelStyle}>
        {label}
      </Label>
      <div className="flex items-center gap-2 mt-1">
        <span
          className={`flex-1 ${value ? 'text-gray-700' : 'text-gray-400 italic'} ${error ? 'text-red-500' : ''}`}
          style={valueStyle}
        >
          {value || 'Click to add brand...'}
        </span>
        <Edit3 className="h-3.5 w-3.5 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
      </div>
    </div>
  )
}
